import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
// Cache imports removed - fetching directly from Firebase without cache

const ArtworkSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedArtwork,
  otherSelectedArtwork,
  artMovements,
  artForms,
  artworkTypes
}) => {
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    artMovement: '',
    artCategory: '',
    artForm: '',
    timePeriod: '',
    typeOfArtwork: ''
  });

  // Fetch artworks from artHistoryArtworks collection - no cache, direct Firebase fetch
  const fetchArtworks = async () => {
    try {
      setLoading(true);
      console.log('🔄 [ARTWORK SELECTION] Fetching artworks from Firebase...');
      
      // Try with orderBy first (for artworks with createdAt)
      let querySnapshot;
      try {
        const q = query(
          collection(db, 'artHistoryArtworks'),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
        console.log('✅ [ARTWORK SELECTION] Query with orderBy succeeded:', querySnapshot.docs.length, 'documents');
      } catch (orderByError) {
        console.warn('⚠️ [ARTWORK SELECTION] orderBy query failed, trying without orderBy:', orderByError);
        // Fallback: fetch without orderBy if createdAt index is missing
        const q = query(collection(db, 'artHistoryArtworks'));
        querySnapshot = await getDocs(q);
        console.log('✅ [ARTWORK SELECTION] Query without orderBy succeeded:', querySnapshot.docs.length, 'documents');
      }
      
      const artworksData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure createdAt exists (use updatedAt or current time as fallback)
        if (!data.createdAt) {
          data.createdAt = data.updatedAt || new Date();
        }
        return {
          id: doc.id,
          ...data
        };
      });
      
      // Sort by createdAt if available (client-side fallback)
      artworksData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.getTime ? a.createdAt.getTime() : 0);
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.getTime ? b.createdAt.getTime() : 0);
        return bTime - aTime; // Descending order
      });
      
      console.log('✅ [ARTWORK SELECTION] Fetched art history artworks:', artworksData.length);
      if (artworksData.length > 0) {
        console.log('📋 [ARTWORK SELECTION] Sample artwork:', artworksData[0]);
        console.log('📋 [ARTWORK SELECTION] All artworks:', artworksData);
      } else {
        console.warn('⚠️ [ARTWORK SELECTION] No artworks found in collection!');
        console.warn('⚠️ [ARTWORK SELECTION] Check Firebase console - artHistoryArtworks collection exists?');
      }
      
      setArtworks(artworksData);
      setFilteredArtworks(artworksData);
    } catch (error) {
      console.error('❌ [ARTWORK SELECTION] Error fetching art history artworks:', error);
      console.error('Error details:', error.message, error.code);
      setArtworks([]);
      setFilteredArtworks([]);
    } finally {
      setLoading(false);
    }
  };


  // Apply filters
  const applyFilters = () => {
    let filtered = artworks;
    console.log('Starting with artworks:', filtered.length);

    // Filter out the other selected artwork to prevent duplicates
    if (otherSelectedArtwork) {
      filtered = filtered.filter(artwork => artwork.id !== otherSelectedArtwork.id);
      console.log('After removing other selected:', filtered.length);
    }

    // Apply other filters
    if (filters.artMovement) {
      filtered = filtered.filter(artwork => 
        artwork.artMovement && artwork.artMovement === filters.artMovement
      );
      console.log('After art movement filter:', filtered.length, 'filter:', filters.artMovement);
    }

    if (filters.artCategory) {
      filtered = filtered.filter(artwork => 
        (artwork.artCategory || movementToCategory(artwork.artMovement)) === filters.artCategory
      );
      console.log('After category filter:', filtered.length, 'filter:', filters.artCategory);
    }

    if (filters.artForm) {
      filtered = filtered.filter(artwork => 
        artwork.artForm && artwork.artForm === filters.artForm
      );
      console.log('After art form filter:', filtered.length, 'filter:', filters.artForm);
    }

    if (filters.timePeriod) {
      filtered = filtered.filter(artwork => 
        artwork.timePeriod && artwork.timePeriod.toLowerCase().includes(filters.timePeriod.toLowerCase())
      );
      console.log('After time period filter:', filtered.length, 'filter:', filters.timePeriod);
    }

    if (filters.typeOfArtwork) {
      filtered = filtered.filter((artwork) => {
        const t = artwork.typeOfArtwork;
        if (!t) return false;
        if (t === filters.typeOfArtwork) return true;
        // Club legacy "Flower painting" under Floral
        if (filters.typeOfArtwork === 'Floral' && /flower painting/i.test(t)) return true;
        // Club legacy "Animal" labels under Animal/Birds
        if (filters.typeOfArtwork === 'Animal/Birds' && /^animal(\s*\/\s*birds)?s?$/i.test(t)) return true;
        return false;
      });
      console.log('After type filter:', filtered.length, 'filter:', filters.typeOfArtwork);
    }

    console.log('Final filtered artworks:', filtered.length);
    setFilteredArtworks(filtered);
    setCurrentIndex(0);
  };

  useEffect(() => {
    if (isOpen) {
      fetchArtworks();
    }
  }, [isOpen]);

  useEffect(() => {
    applyFilters();
  }, [filters, artworks, otherSelectedArtwork]);

  // Debug logging for current artwork changes
  useEffect(() => {
    if (isOpen && filteredArtworks.length > 0) {
      const currentArtwork = filteredArtworks[currentIndex];
      if (currentArtwork) {
        console.log('Current artwork changed:', {
          index: currentIndex,
          title: currentArtwork.title,
          imageUrl: currentArtwork.imageUrl,
          id: currentArtwork.id
        });
      }
    }
  }, [currentIndex, filteredArtworks, isOpen]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleArtworkSelect = (artwork) => {
    onSelect(artwork);
  };

  const nextArtwork = () => {
    setCurrentIndex(prev => {
      const newIndex = prev < filteredArtworks.length - 1 ? prev + 1 : 0;
      console.log('Next artwork - current index:', prev, 'new index:', newIndex, 'total artworks:', filteredArtworks.length);
      return newIndex;
    });
  };

  const prevArtwork = () => {
    setCurrentIndex(prev => {
      const newIndex = prev > 0 ? prev - 1 : filteredArtworks.length - 1;
      console.log('Prev artwork - current index:', prev, 'new index:', newIndex, 'total artworks:', filteredArtworks.length);
      return newIndex;
    });
  };

  const clearFilters = () => {
    setFilters({
      artMovement: '',
      artForm: '',
      timePeriod: '',
      typeOfArtwork: ''
    });
  };

  if (!isOpen) return null;

  const currentArtwork = filteredArtworks[currentIndex];

  // Map known movements to high-level categories for older records
  const movementToCategory = (movement) => {
    if (!movement) return '';
    const m = movement.toLowerCase();
    if (['ancient egyptian art','early dynastic period','old kingdom','1st intermediate period','middle kingdom','2nd intermediate period','new kingdom','amarna period','3rd intermediate period','late period','ptolemaic period','roman period'].some(k=>m.includes(k))) return 'Ancient Egyptian Art';
    if (['ancient greek art','geometric period','archaic period','classical period','hellenistic period'].some(k=>m.includes(k))) return 'Ancient Greek Art';
    if (['byzantine','pre-romaneseque','romanesque','gothic','medieval','coptic'].some(k=>m.includes(k))) return 'Western Medieval Art';
    if (['proto renaissance','early renaissance','high renaissance','mannerism','northern renaissance'].some(k=>m.includes(k))) return 'Western Renaissance Art';
    if (['baroque','dutch golden age','rococo','neoclassicism','romanticism','orientalism','academic art','realism'].some(k=>m.includes(k))) return 'Western Post Renaissance Art';
    if (['modern','pointillism','neo-suprematism','naive art','naïve art','spectralism','post-impressionism','impressionism','fauvism','cubism','abstract','expressionism','futurism','dada','surrealism','constructivism','de stijl','bauhaus','art deco','precisionism','purism','rayonism','metaphysical','suprematism'].some(k=>m.includes(k))) return 'Modern Art';
    if (['contemporary'].some(k=>m.includes(k))) return 'Contemporary Art';
    if (['dynasty','china','ink'].some(k=>m.includes(k))) return 'Chinese Art';
    if (['korean','joseon','dansaekhwa','informel'].some(k=>m.includes(k))) return 'Korean Art';
    if (['japanese','edo','meiji','taishō','shōwa','muromachi','azuchi-momoyama'].some(k=>m.includes(k))) return 'Japanese Art';
    if (['islamic','abbasid','ilkhanid','timurid','ottoman','safavid','mughal','qajar'].some(k=>m.includes(k))) return 'Islamic Art';
    if (['native','yoruba','folk'].some(k=>m.includes(k))) return 'Native Art';
    if (['pre-columbian','post-classic'].some(k=>m.includes(k))) return 'Pre-Columbian Art';
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-amber-500 text-white p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Choose Artwork</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-amber-200 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Filters */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Filter Artworks</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Art Movement Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Art Movement
                </label>
                <select
                  value={filters.artMovement}
                  onChange={(e) => handleFilterChange('artMovement', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800 bg-white"
                >
                  <option value="">All Movements</option>
                  {artMovements.map(movement => (
                    <option key={movement} value={movement}>{movement}</option>
                  ))}
                </select>
              </div>

              {/* Art Form Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Art Form
                </label>
                <select
                  value={filters.artForm}
                  onChange={(e) => handleFilterChange('artForm', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800 bg-white"
                >
                  <option value="">All Forms</option>
                  {artForms.map(form => (
                    <option key={form} value={form}>{form}</option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Type
                </label>
                <select
                  value={filters.typeOfArtwork}
                  onChange={(e) => handleFilterChange('typeOfArtwork', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800 bg-white"
                >
                  <option value="">All Types</option>
                  {artworkTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                  {!artworkTypes.includes('Miniature') && (
                    <option value="Miniature">Miniature</option>
                  )}
                  {!artworkTypes.includes('Calligraphy') && (
                    <option value="Calligraphy">Calligraphy</option>
                  )}
                  {!artworkTypes.includes('Animal/Birds') && (
                    <option value="Animal/Birds">Animal/Birds</option>
                  )}
                  {!artworkTypes.includes('Sculpture') && (
                    <option value="Sculpture">Sculpture</option>
                  )}
                  {!artworkTypes.includes('Interior') && (
                    <option value="Interior">Interior</option>
                  )}
                </select>
              </div>

              {/* Time Period Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Time Period
                </label>
                <input
                  type="text"
                  value={filters.timePeriod}
                  onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
                  placeholder="e.g., Renaissance"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800 bg-white placeholder-gray-400"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Clear Filters
              </button>
              <span className="text-sm text-gray-700 font-medium">
                {filteredArtworks.length} artworks found
              </span>
            </div>
          </div>

          {/* Artwork Display */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading artworks...</p>
            </div>
          ) : artworks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">No art history artworks found in the database.</p>
              <p className="text-sm text-gray-400">Please add some artworks through the Admin Panel first.</p>
            </div>
          ) : filteredArtworks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No artworks found matching your filters.</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your filter criteria or click "Show All".</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Select Artwork Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Select Artwork</h3>
                
                {/* Artwork Carousel */}
                <div className="mb-4">
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {filteredArtworks.map((artwork, index) => (
                      <div
                        key={artwork.id}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          index === currentIndex 
                            ? 'border-amber-500 shadow-lg' 
                            : 'border-gray-200 hover:border-amber-300'
                        }`}
                        onClick={() => setCurrentIndex(index)}
                      >
                        <img
                          src={artwork.imageUrl}
                          alt={artwork.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-gray-600">
                      {currentIndex + 1} of {filteredArtworks.length} artworks
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={prevArtwork}
                        className="p-1 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
                      >
                        ←
                      </button>
                      <button
                        onClick={nextArtwork}
                        className="p-1 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
                      >
                        →
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selected Artwork Display */}
              {currentArtwork && (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                  <div className="flex items-center justify-center">
                    <div className="max-w-md">
                      <img
                        key={`${currentArtwork.id}-${currentIndex}`}
                        src={currentArtwork.imageUrl}
                        alt={currentArtwork.title}
                        className="w-full h-auto rounded-lg shadow-lg"
                        onLoad={() => console.log('Image loaded for:', currentArtwork.title)}
                        onError={() => console.log('Image failed to load for:', currentArtwork.title, currentArtwork.imageUrl)}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => handleArtworkSelect(currentArtwork)}
                      className="bg-amber-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-600 transition-colors"
                    >
                      Select This Artwork
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtworkSelectionModal;
